import React, { useEffect, useState } from 'react';
import AdminGuard from '../components/AdminGuard';
import AdminLayout from '../components/AdminLayout';
import { api } from '../../../api/client';

type HealthStatus = 'healthy' | 'degraded' | 'unhealthy';

interface ServiceCheck {
  status: HealthStatus;
}

interface HealthData {
  status: HealthStatus;
  uptime: number;
  version?: string;
  environment?: string;
  checks?: Record<string, ServiceCheck>;
}

interface MemoryMetrics {
  heapUsed: number;
  heapTotal: number;
  rss: number;
  external: number;
}

interface RequestMetrics {
  total?: number;
  errors?: number;
  avgResponseTime?: number;
}

interface WebSocketMetrics {
  connections?: number;
  rooms?: number;
}

interface SchedulerMetrics {
  activeTimers?: number;
  scheduledJobs?: number;
  lastRun?: string;
}

interface SystemMetrics {
  memory?: MemoryMetrics;
  requests?: RequestMetrics;
  websockets?: WebSocketMetrics;
  scheduler?: SchedulerMetrics;
}

interface JobCountByState {
  name: string;
  state: string;
  count: number;
}

interface UpcomingJob {
  name: string;
  id: string;
  startAfter: string;
}

interface CompletedJob {
  name: string;
  id: string;
  completedOn: string;
}

interface QueueDiagnostics {
  queues: string[];
  jobCountsByState: JobCountByState[];
  upcomingJobs: UpcomingJob[];
  recentlyCompleted: CompletedJob[];
  serverTime: string;
  error?: string;
}

const AdminSystemPage: React.FC = () => {
  const [health, setHealth] = useState<HealthData | null>(null);
  const [metrics, setMetrics] = useState<SystemMetrics | null>(null);
  const [queueDiagnostics, setQueueDiagnostics] = useState<QueueDiagnostics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [healthResponse, metricsResponse, queueResponse] = await Promise.all([
        api.get('/admin/system/health') as Promise<HealthData>,
        api.get('/admin/system/metrics') as Promise<SystemMetrics>,
        api.get('/admin/queue/diagnostics') as Promise<QueueDiagnostics>,
      ]);
      setHealth(healthResponse);
      setMetrics(metricsResponse);
      setQueueDiagnostics(queueResponse);
    } catch (err) {
      const apiError = err as { message?: string };
      setError(apiError.message || 'Failed to load system data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const formatUptime = (seconds: number): string => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  const getStatusColor = (status: HealthStatus): string => {
    switch (status) {
      case 'healthy':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'degraded':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'unhealthy':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  return (
    <AdminGuard>
      <AdminLayout>
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">System Health</h2>
            <button
              onClick={fetchData}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600"
            >
              Refresh
            </button>
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 p-4 rounded-lg">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500 dark:text-gray-400">Loading...</div>
            </div>
          ) : (
            <>
              {/* Health Status */}
              {health && (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">Service Status</h3>
                    <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(health.status)}`}>
                      {health.status?.charAt(0).toUpperCase() + health.status?.slice(1)}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Uptime</div>
                      <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">
                        {formatUptime(health.uptime)}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Version</div>
                      <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">
                        {health.version || 'Unknown'}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Environment</div>
                      <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">
                        {health.environment || 'Unknown'}
                      </div>
                    </div>
                  </div>

                  {/* Service Checks */}
                  {health.checks && (
                    <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-6">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">Service Checks</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {Object.entries(health.checks).map(([name, check]) => (
                          <div key={name} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                            <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">
                              {name.replace(/_/g, ' ')}
                            </span>
                            <span className={`px-2 py-0.5 text-xs font-medium rounded ${getStatusColor(check.status)}`}>
                              {check.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Memory Usage */}
              {metrics && metrics.memory && (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-6">Memory Usage</h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <div>
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Heap Used</div>
                      <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">
                        {formatBytes(metrics.memory.heapUsed)}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Heap Total</div>
                      <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">
                        {formatBytes(metrics.memory.heapTotal)}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400">RSS</div>
                      <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">
                        {formatBytes(metrics.memory.rss)}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400">External</div>
                      <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">
                        {formatBytes(metrics.memory.external)}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Request Metrics */}
              {metrics && metrics.requests && (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-6">Request Metrics</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Requests</div>
                      <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">
                        {metrics.requests.total?.toLocaleString() || 0}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Errors</div>
                      <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">
                        {metrics.requests.errors?.toLocaleString() || 0}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Avg Response Time</div>
                      <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">
                        {metrics.requests.avgResponseTime?.toFixed(0) || 0}ms
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* WebSocket Connections */}
              {metrics && metrics.websockets !== undefined && (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-6">WebSocket Connections</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Active Connections</div>
                      <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">
                        {metrics.websockets?.connections?.toLocaleString() || 0}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Rooms</div>
                      <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">
                        {metrics.websockets?.rooms?.toLocaleString() || 0}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Job Queue Status (pg-boss) */}
              {queueDiagnostics && (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">Job Queue (pg-boss)</h3>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Server: {new Date(queueDiagnostics.serverTime).toLocaleTimeString()}
                    </span>
                  </div>

                  {queueDiagnostics.error ? (
                    <div className="text-red-600 dark:text-red-400">{queueDiagnostics.error}</div>
                  ) : (
                    <>
                      {/* Job counts by state */}
                      <div className="mb-6">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Jobs by State</h4>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead>
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Queue</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">State</th>
                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Count</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                              {queueDiagnostics.jobCountsByState.length === 0 ? (
                                <tr>
                                  <td colSpan={3} className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 text-center">
                                    No jobs in queue
                                  </td>
                                </tr>
                              ) : (
                                queueDiagnostics.jobCountsByState.map((job, idx) => (
                                  <tr key={idx}>
                                    <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">{job.name}</td>
                                    <td className="px-3 py-2">
                                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                                        job.state === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                                        job.state === 'active' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
                                        job.state === 'created' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' :
                                        job.state === 'failed' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' :
                                        'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                      }`}>
                                        {job.state}
                                      </span>
                                    </td>
                                    <td className="px-3 py-2 text-sm text-gray-900 dark:text-white text-right">{job.count}</td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Upcoming jobs */}
                      <div className="mb-6">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Upcoming Jobs (next 5 min)</h4>
                        {queueDiagnostics.upcomingJobs.length === 0 ? (
                          <div className="text-sm text-gray-500 dark:text-gray-400">No jobs scheduled in the next 5 minutes</div>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                              <thead>
                                <tr>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Queue</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Scheduled For</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {queueDiagnostics.upcomingJobs.map((job) => (
                                  <tr key={job.id}>
                                    <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">{job.name}</td>
                                    <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">
                                      {new Date(job.startAfter).toLocaleTimeString()}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>

                      {/* Recently completed */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Recently Completed (last 10 min)</h4>
                        {queueDiagnostics.recentlyCompleted.length === 0 ? (
                          <div className="text-sm text-gray-500 dark:text-gray-400">No jobs completed in the last 10 minutes</div>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                              <thead>
                                <tr>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Queue</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Completed At</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {queueDiagnostics.recentlyCompleted.map((job) => (
                                  <tr key={job.id}>
                                    <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">{job.name}</td>
                                    <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">
                                      {new Date(job.completedOn).toLocaleTimeString()}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </AdminLayout>
    </AdminGuard>
  );
};

export default AdminSystemPage;
